import React, {useEffect, useState} from 'react';
import {Link, Paper} from '@mui/material';
import {useMapContext} from '../../contexts/MapContext';
import Help from "../../utils/Help";
import {useLIDContext} from "../../contexts/LIDContext";
import {RG_COST_RATE, GR_COST_RATE,  PLANTER_COST_RATE, RB_COST_RATE} from "../LID/LIDCosts";
import {fetchSystemResults, fetchSystemTotals} from "./SystemTotalHelpers";

const SystemTotalsPanel = () => {
    const {outFileExists, setMouseDown, setRunModelGuard, selectedStormOption, userData, devPlanID, startOperation, finishOperation} = useMapContext();
    const { totalCost } = useLIDContext();

    const [systemTotals, setSystemTotals] = useState({
        flooding: null, // State for the system totals flooding data
        infiltration: null, // State for the system totals infiltration data
        outflow: null, // State for the system totals outflow data
        runoff: null, // State for the system totals runoff data
    })

    const [systemResults, setSystemResults] = useState({
        flooding: null, // State for the system results flooding data
        infiltration: null, // State for the system results infiltration data
        outflow: null, // State for the system results outflow data
        runoff: null, // State for the system results runoff data
    });

    const systemDiff = React.useMemo(() => {
        const diff = {flooding: null, infiltration: null, outflow: null, runoff: null};
        for (const key in systemResults) {
            if (systemResults[key] !== null && systemTotals[key] !== null) {
                diff[key] = systemResults[key] - systemTotals[key];
            }
        }
        return diff;
    }, [systemResults, systemTotals]);


    // Populates the systemTotals state with system-wide baseline data for the selected storm
    useEffect(() => {
        try {
            startOperation();
            setSystemTotals({ flooding: null, infiltration: null, outflow: null, runoff: null })
            fetchSystemTotals(selectedStormOption).then(data => {
                setSystemTotals(data);
            });
        } finally {
            finishOperation();
        }
    }, [selectedStormOption]);

    // Populates the systemResults state with system-wide results data for the selected storm
    // this only runs if an outFile exists for the specific storm option and development plan selected
    useEffect(() => {
        setRunModelGuard(false);
        try {
            startOperation();
            setSystemResults({ flooding: null, infiltration: null, outflow: null, runoff: null });
            if (outFileExists && userData && devPlanID) {
                fetchSystemResults(selectedStormOption, userData, devPlanID).then(data => {
                    setSystemResults(data);
                });
            }
        } finally {
            finishOperation();
        }
        setRunModelGuard(true);
    }, [outFileExists, devPlanID, systemTotals]);


    const formatValue = (value) => {
        return value != null ? value.toFixed(0) : '-';
    };

    const formatDiff = (value, key) => {
        if (value == null) return '';
        const formattedValue = value.toFixed(2);
        const prefix = value > 0 ? '+' : '';
        return `${prefix}${formattedValue}`;
    };

    const red = '#f76060';
    const green = '#78f178';
    const getDiffColor = (value, key) => {
        if (value == null) return 'inherit';
        if (key === 'infiltration') {
            return value > 0 ? green : red;
        }
        return value < 0 ? green : red;
    };

    const paperStyle = {
        backgroundColor: '#071317',
        width: '120px',
        height: '65px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '5px 0',
    };

    const textStyle = {
        textAlign: 'center',
        color: '#94cfec',
        fontSize: '0.9em',
    };
    // Hide flooding for now: { icon: '💧', label: 'Flooding', key: 'flooding' },
    const metrics = [
        { icon: '🌊', label: 'Runoff', key: 'runoff' },
        { icon: '🌱', label: 'Infiltration', key: 'infiltration' },
        { icon: '🚱', label: 'Overflow', key: 'outflow' },
    ];

    return (
        <div
          onMouseEnter={() => setMouseDown(false)}
            style={{
            position: 'absolute',
            top: '35%',
            transform: 'translateY(-50%)',
            right: 20,
            width: '140px',
            opacity: 0.9,
            zIndex: 10,
            borderRadius: '5px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '10px',
        }}
        >
            <div style={{
                color: '#94cfec',
                fontSize: '1em',
                fontWeight: 'bold',
                marginBottom: '10px',
                textAlign: 'center'
            }}>
                City-wide Summary <Help text="These totals are from a historical storm with 3 inches of rain over 10 hours."/>
            </div>
            {metrics.map(({ icon, label, key }) => (
                <Paper key={key} elevation={3} style={paperStyle} >
                    <div style={textStyle}>
                        {icon} {label}: <br />
                            <>
                                <b>{formatValue(systemTotals?.[key])}</b> MG <br/>
                            </>

                        {outFileExists && systemDiff[key] !== null && (
                            <span style={{ color: getDiffColor(systemDiff[key], key) }}>
                                {formatDiff(systemDiff[key], key)} MG
                            </span>
                        )}
                        {outFileExists && systemDiff[key] === null && (
                            <span style={{ color: "inherit" }}>
                                --- MG
                            </span>
                        )}
                    </div>
                </Paper>
            ))}
            <div>
                <Paper key={'Estimated Cost'} elevation={3} style={
                    {
                        backgroundColor: '#151c14',
                        width: '120px',
                        height: '65px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        margin: '10px 0',
                    }
                } >
                    <div style={{color: '#bddcaa', fontSize: '0.8em', textAlign: 'center', padding: '0 10px'}}>
                        {"Est. Cost"}:
                        <Help text={<div>
                            This (rough) estimation is from a <Link color={"#8bd3f5"} href={"https://drive.google.com/file/d/1WmxS6ivlrh0pmxd_Myz4fKr0fwIDkxpm/view?usp=sharing"}>2015 study</Link> funded by the US EPA. <br/>
                            Adjusted for inflation as of May 2025.<br/>
                            Rain Gardens: ${RG_COST_RATE.toFixed(2)} / sqft <br/>
                            Green Roofs: ${GR_COST_RATE.toFixed(2)} / sqft <br/>
                            Planters: ${PLANTER_COST_RATE.toFixed(2)} / sqft <br/>
                            Rain barrels: ${RB_COST_RATE.toFixed(2)} / gallon
                        </div>
                        }/>
                        <br />
                        <b>
                            {totalCost ? Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(parseFloat(totalCost)) : '---'}
                        </b>
                    </div>
                </Paper>
            </div>
        </div>


    );
};

export default SystemTotalsPanel;